import React, { useCallback, useMemo } from 'react';
import toast, { ToastPosition } from 'react-hot-toast';
import Notification from '~/components/Commons/Notification/Notification';

export type NotificationLevels = 'info' | 'warning' | 'success' | 'danger' | 'primary';
type NotificationMessage = string;

// re-export ToastPosition for consumers
export type NotificationPosition = ToastPosition;

// available positions from react-hot-toast:
// 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'
// fixed notifications (setNotification): banner top center
// timed notifications (setTimedNotification): toast top-center
const BANNER_POSITION: NotificationPosition = 'top-center';
const TIMED_POSITION: NotificationPosition = 'top-center';

// stable id for persistent notifications - ensures only one persistent notification at a time
const PERSISTENT_TOAST_ID = 'persistent-notification';

// stack limit for timed notifications (global, across all levels)
const MAX_TIMED_NOTIFICATIONS = 3;

export type SetNotification = (
  open: boolean,
  notificationLevel: NotificationLevels,
  notificationMessage: NotificationMessage,
  position?: NotificationPosition,
  action?: () => void,
  actionLabel?: string,
) => void;

type CloseNotification = () => void;

type SetTimedNotification = (
  notificationLevel: NotificationLevels,
  notificationMessage: NotificationMessage,
  timeout?: number,
  position?: NotificationPosition,
  action?: () => void,
  actionLabel?: string,
) => void;

type NotificationType = {
  setNotification: SetNotification;
  closeNotification: CloseNotification;
  setTimedNotification: SetTimedNotification;
};

const Notifications = React.createContext<NotificationType | null>(
  null,
) as React.Context<NotificationType>;

type PersistentPayload = {
  level: NotificationLevels;
  message: NotificationMessage;
  position: NotificationPosition;
  action?: () => void;
  actionLabel?: string;
};

// provider component for the notifications context:
// wraps App component (root)
// must wrap test components that require context
export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  // timed notifications: FIFO of timed notification IDs only (max: 3)
  // FIFO of timed notification IDs only (persistent banner excluded)
  const timedNotificationQueue = React.useRef<string[]>([]);
  const idCounter = React.useRef(0);

  const nextTimedId = useCallback((level: NotificationLevels) => {
    idCounter.current += 1;
    return `timed-${level}-${Date.now()}-${idCounter.current}`;
  }, []);

  // persistent banners (queue, singleton display): FIFO
  const persistentQueue = React.useRef<PersistentPayload[]>([]);
  const persistentVisible = React.useRef(false);

  // Used to prevent race when multiple closes/opens happen quickly
  const persistentEpoch = React.useRef(0);

  // Ref indirection breaks the circular dependency between showPersistent and dismissAndAdvance
  const showPersistentRef = React.useRef<((payload: PersistentPayload) => void) | null>(null);

  const dismissAndAdvance = useCallback(() => {
    toast.dismiss(PERSISTENT_TOAST_ID);
    persistentVisible.current = false;

    persistentEpoch.current += 1;
    const epochAtSchedule = persistentEpoch.current;

    setTimeout(() => {
      if (persistentEpoch.current !== epochAtSchedule) return;
      const next = persistentQueue.current.shift();
      if (next) showPersistentRef.current?.(next);
    }, 0);
  }, []);

  const showPersistent = useCallback(
    (payload: PersistentPayload) => {
      persistentVisible.current = true;

      toast.custom(
        (t) => (
          <Notification
            level={payload.level}
            message={payload.message}
            t={t}
            action={payload.action}
            actionLabel={payload.actionLabel}
            onClose={dismissAndAdvance}
          />
        ),
        {
          id: PERSISTENT_TOAST_ID,
          duration: Infinity,
          position: payload.position,
        },
      );
    },
    [dismissAndAdvance],
  );

  showPersistentRef.current = showPersistent;

  const enqueueOrShowPersistent = useCallback(
    (payload: PersistentPayload) => {
      if (!persistentVisible.current) {
        showPersistent(payload);
        return;
      }
      persistentQueue.current.push(payload);
    },
    [showPersistent],
  );

  const dismissPersistentAndAdvance = useCallback(() => {
    if (!persistentVisible.current) return;
    dismissAndAdvance();
  }, [dismissAndAdvance]);

  const setNotification: SetNotification = useCallback(
    (open, level, message, position = BANNER_POSITION, action = undefined, actionLabel = '') => {
      if (open) {
        enqueueOrShowPersistent({
          level,
          message,
          position,
          action,
          actionLabel,
        });
      } else {
        dismissPersistentAndAdvance();
      }
    },
    [enqueueOrShowPersistent, dismissPersistentAndAdvance],
  );

  const closeNotification: CloseNotification = useCallback(() => {
    dismissPersistentAndAdvance();
  }, [dismissPersistentAndAdvance]);

  const setTimedNotification: SetTimedNotification = useCallback(
    (
      level,
      message = '',
      time = 5000,
      position = TIMED_POSITION,
      action = undefined,
      actionLabel = '',
    ) => {
      // enforce global cap. queue may contain stale ids (already auto-removed)
      // removing a stale id is a no-op, so process stays safe without side effects
      while (timedNotificationQueue.current.length >= MAX_TIMED_NOTIFICATIONS) {
        const oldestId = timedNotificationQueue.current.shift();
        if (oldestId) toast.remove(oldestId);
      }

      const id = nextTimedId(level);
      timedNotificationQueue.current.push(id);
      toast.custom(
        (t) => (
          <Notification
            level={level}
            message={message}
            t={t}
            action={action}
            actionLabel={actionLabel}
          />
        ),
        {
          id,
          duration: time,
          position,
        },
      );
    },
    [nextTimedId],
  );

  const value = useMemo(
    () => ({
      setNotification,
      closeNotification,
      setTimedNotification,
    }),
    [setNotification, closeNotification, setTimedNotification],
  );

  return <Notifications.Provider value={value}>{children}</Notifications.Provider>;
}

// consumer hook for notifications context
const useNotifications = () => {
  const context = React.useContext(Notifications);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export default useNotifications;
